// src/components/PickerComponent.js
import React, { useEffect, useMemo, useRef, useState } from "react";
import "./PickerComponent.css";
import RollingPicker from "./RollingPicker";

function rangeInt(min, max) {
  const out = [];
  for (let i = min; i <= max; i++) out.push(String(i));
  return out;
}

function rangeDecimal(min, max, step) {
  const out = [];
  const kMin = Math.round(min / step);
  const kMax = Math.round(max / step);
  const decimals = step % 1 === 0 ? 0 : 1;
  for (let k = kMin; k <= kMax; k++) out.push((k * step).toFixed(decimals));
  return out;
}

export default function PickerComponent({
  open,
  title,
  repsValue,
  weightValue,
  onConfirm,
  onCancel,
}) {
  const repsOptions = useMemo(() => rangeInt(0, 60), []);
  const weightOptions = useMemo(() => rangeDecimal(0, 200, 0.5), []);

  const [pickerValue, setPickerValue] = useState({
    reps: repsOptions[0],
    weight: weightOptions[0],
  });

  const readyRef = useRef(false);
  const didInteractRef = useRef(false);

  const tRef = useRef(null);
  const lastSentRef = useRef("");

  useEffect(() => {
    if (!open) return;

    readyRef.current = false;
    didInteractRef.current = false;

    const nextReps =
      repsValue === "" || repsValue == null ? repsOptions[0] : String(repsValue);
    const nextWeight =
      weightValue === "" || weightValue == null ? weightOptions[0] : String(weightValue);

    const safeReps = repsOptions.includes(nextReps) ? nextReps : repsOptions[0];
    const safeWeight = weightOptions.includes(nextWeight) ? nextWeight : weightOptions[0];

    setPickerValue({ reps: safeReps, weight: safeWeight });
    lastSentRef.current = `${safeReps}|${safeWeight}`;

    const id = setTimeout(() => {
      readyRef.current = true;
    }, 0);

    return () => clearTimeout(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const setReps = (nextReps) => {
    didInteractRef.current = true;
    setPickerValue((prev) => ({ ...prev, reps: String(nextReps) }));
  };

  const setWeight = (nextWeight) => {
    didInteractRef.current = true;
    setPickerValue((prev) => ({ ...prev, weight: String(nextWeight) }));
  };

  useEffect(() => {
    if (!open) return;
    if (!readyRef.current) return;
    if (!didInteractRef.current) return;

    const key = `${pickerValue.reps}|${pickerValue.weight}`;
    if (key === lastSentRef.current) return;

    if (tRef.current) clearTimeout(tRef.current);
    tRef.current = setTimeout(() => {
      lastSentRef.current = key;
      onConfirm?.({
        reps: Number(pickerValue.reps),
        weight: Number(pickerValue.weight),
      });
    }, 120);

    return () => {
      if (tRef.current) clearTimeout(tRef.current);
    };
  }, [open, pickerValue.reps, pickerValue.weight, onConfirm]);

  if (!open) return null;

  return (
    <div className="pc-backdrop glass" onMouseDown={onCancel}>
      <div className="pc-sheet glass" onMouseDown={(e) => e.stopPropagation()}>
        <div className="pc-head">
          <div className="pc-title">{title}</div>
          <button className="pc-close" type="button" onClick={onCancel}>
            ✕
          </button>
        </div>

        <div className="pc-wheels--row">
          <RollingPicker
            name="reps"
            label="Reps"
            options={repsOptions}
            value={pickerValue.reps}
            unit="reps"
            onValueChange={setReps}
          />

          <RollingPicker
            name="weight"
            label="Weight"
            options={weightOptions}
            value={pickerValue.weight}
            unit="kg"
            onValueChange={setWeight}
          />
        </div>
      </div>
    </div>
  );
}